import { useRef, useState } from "react";
import { slideConfirmed, slideProgress } from "@helm/core";

/**
 * "Slide to close/flatten" (SPEC §17.7): the thumb must travel the full track before release
 * confirms. A partial drag springs back. Never fires from a tap. Used for Exit & Cxl, Cancel
 * All and Flatten on mobile. Cancellation or a disabled control never confirms. Flatten takes the
 * danger-exit tone (`flatten`), never the Sell colour (owner 2026-10-04, as the desktop Flatten).
 */
export function SlideToConfirm({
  label,
  tone = "sell",
  disabled,
  onConfirm,
}: {
  label: string;
  tone?: "buy" | "sell" | "neutral" | "flatten";
  disabled?: boolean;
  onConfirm: () => void;
}): React.JSX.Element {
  const track = useRef<HTMLDivElement | null>(null);
  const startX = useRef<number | null>(null);
  const [progress, setProgress] = useState(0);
  const knob = 48;

  const width = (): number => track.current?.getBoundingClientRect().width ?? 0;

  const onDown = (e: React.PointerEvent): void => {
    if (disabled) return;
    startX.current = e.clientX;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent): void => {
    if (startX.current === null) return;
    setProgress(slideProgress(startX.current, e.clientX, width(), knob));
  };
  const onUp = (): void => {
    if (startX.current === null) return;
    const confirmed = slideConfirmed(progress);
    startX.current = null;
    setProgress(0);
    if (confirmed && !disabled) onConfirm();
  };

  return (
    <div
      ref={track}
      className={`slide slide-${tone}${disabled ? " slide-disabled" : ""}`}
      role="button"
      aria-disabled={disabled}
      aria-label={label}
    >
      <div className="slide-fill" style={{ width: `calc(${String(progress * 100)}% + ${String(knob)}px)` }} />
      <span className="slide-label">{disabled ? `${label} (disabled)` : `Slide to ${label}`}</span>
      <div
        className="slide-knob"
        style={{ left: `calc(${String(progress * 100)}% - ${String(progress * knob)}px + 3px)` }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={() => {
          startX.current = null;
          setProgress(0);
        }}
      >
        ›
      </div>
    </div>
  );
}
